import ProductCell from "@/core/components/ProductCell";
import {
	type AutocompleteFetcher,
	Badge,
	Button,
	Can,
	DataTable,
	type DataTableFiltersProp,
} from "@k3-tech/react-kit";
import type { ColumnDef, PaginationState } from "@tanstack/react-table";
import { PlusIcon, RotateCcwIcon } from "lucide-react";
import { useMemo } from "react";
import type { BranchProductSalesChannelItemRow } from "../usecases/useBranchProductSalesChannelItemList";
import {
	BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE,
	BRANCH_PRODUCT_ITEM_TYPE_OUT_OF_STOCK,
} from "../usecases/useBranchProductSalesChannelItemMutations";

export interface UnavailableItemTableProps {
	data: BranchProductSalesChannelItemRow[];
	loading: boolean;
	onRefresh: () => void;
	onFormFilterChange?: (values: Record<string, unknown>) => void;
	formFilterValues?: Record<string, unknown> | null;
	paginationState: PaginationState;
	onPaginationChange?: (paginationState: PaginationState) => void;
	rowCount?: number | null;
	branchAutocompleteFetcher: AutocompleteFetcher;
	branchOptions?: { value: string; label: string }[];
	channelOptions: { value: string; label: string }[];
	channelNameByID: Map<string, string>;
	selectedChannelID: string;
	onRestore: (record: BranchProductSalesChannelItemRow) => void;
	onAdd: () => void;
}

export const UnavailableItemTable = ({
	data,
	loading,
	onRefresh,
	onFormFilterChange,
	formFilterValues,
	paginationState,
	onPaginationChange,
	rowCount,
	branchAutocompleteFetcher,
	branchOptions,
	channelOptions,
	channelNameByID,
	selectedChannelID,
	onRestore,
	onAdd,
}: UnavailableItemTableProps) => {
	const columns = useMemo<ColumnDef<BranchProductSalesChannelItemRow>[]>(
		() => [
			{ header: "Branch", accessorKey: "branchCode" },
			{
				header: () => <div className="min-w-[300px]">Product</div>,
				accessorKey: "productName",
				cell: ({ row }) => (
					<ProductCell
						productCode={row.original.productCode}
						productName={row.original.productName}
						barcode={row.original.product?.barcode}
					/>
				),
			},
			{
				header: "Channel",
				accessorKey: "salesChannelID",
				cell: ({ row }) => (
					<Badge variant="secondary">
						{channelNameByID.get(row.original.salesChannelID) ??
							row.original.salesChannelID}
					</Badge>
				),
			},
			{ header: "Note", accessorKey: "note" },
			{
				header: "Type",
				accessorKey: "type",
				cell: ({ row }) =>
					row.original.type === BRANCH_PRODUCT_ITEM_TYPE_OUT_OF_STOCK ? (
						<Badge variant="destructive">Out of Stock</Badge>
					) : (
						<Badge variant="outline">Manual</Badge>
					),
			},
			{
				id: "actions",
				header: "Actions",
				cell: ({ row }) => (
					<Can permissions="sales_channel_category_groups.edit">
						<Button
							size="sm"
							variant="ghost"
							onClick={() => onRestore(row.original)}
						>
							<RotateCcwIcon className="h-3.5 w-3.5" />
						</Button>
					</Can>
				),
			},
		],
		[channelNameByID, onRestore],
	);

	const formFilters = useMemo<DataTableFiltersProp>(
		() => [
			{
				variant: "plain",
				layout: "flex",
				flex: { direction: "row", wrap: true, gap: "gap-3" },
				fields: [
					{
						name: "branchID",
						label: " ",
						type: "autocomplete",
						autocompleteMode: "server",
						placeholder: "Search branch",
						options: branchOptions ?? [],
						fetcher: branchAutocompleteFetcher,
					},
					{
						name: "salesChannelID",
						label: " ",
						type: "select",
						className: "w-full",
						placeholder: "Channel",
						options: channelOptions,
						defaultValue: selectedChannelID,
					},
					{
						name: "type",
						label: " ",
						type: "select",
						className: "w-full",
						placeholder: "All types",
						options: [
							{ value: BRANCH_PRODUCT_ITEM_TYPE_EXCLUDE, label: "Manual" },
							{
								value: BRANCH_PRODUCT_ITEM_TYPE_OUT_OF_STOCK,
								label: "Out of Stock",
							},
						],
						defaultValue: "",
					},
				],
			},
		],
		[
			channelOptions,
			branchAutocompleteFetcher,
			branchOptions,
			selectedChannelID,
		],
	);

	return (
		<>
			<div className="mb-3 flex justify-end">
				<Can permissions="sales_channel_category_groups.edit">
					<Button
						size="sm"
						variant="outline"
						onClick={onAdd}
						disabled={!selectedChannelID}
					>
						<PlusIcon className="h-3.5 w-3.5" />
						Add
					</Button>
				</Can>
			</div>
			<div className="w-full overflow-x-auto">
				<DataTable<BranchProductSalesChannelItemRow, unknown>
					columns={columns}
					data={data}
					filterWrapper="none"
					filterShowActionsSeparator={false}
					loading={loading}
					formFilters={formFilters}
					formFilterValues={formFilterValues}
					onFormFilterChange={onFormFilterChange}
					pagination={(rowCount ?? 0) > 0}
					paginationState={paginationState}
					onPaginationChange={onPaginationChange}
					rowCount={rowCount ?? undefined}
					showStandardActions
					onRefresh={onRefresh}
				/>
			</div>
		</>
	);
};
